// @source 40 CFR Part 261 (waste codes); 40 CFR 279 (used oil); 40 CFR 273 (universal waste)
// Reverse lookup: material / common name -> RCRA classification. The honest
// answer for most industrial recyclables is "not federally regulated as
// hazardous" — that page still wins the query and routes to the funnel.
import { getRcraEntry } from './rcraCatalogue';

export interface MaterialRcraContext {
  context: string;
  code?: string;
  note?: string;
}

export interface MaterialRcraEntry {
  slug: string;
  name: string;
  synonyms: string[];
  rcraStatus: 'listed' | 'characteristic-possible' | 'not-regulated';
  summary: string;
  contexts: MaterialRcraContext[];
}

export const MATERIAL_RCRA_LOOKUP: MaterialRcraEntry[] = [
  // ---- Marquee hazardous (F, D, P, U lists) ----
  {
    slug: 'spent-solvents',
    name: 'Spent Solvents',
    synonyms: ['waste solvent', 'used solvent', 'degreasing solvent', 'paint thinner waste'],
    rcraStatus: 'listed',
    summary:
      'Spent solvents are F-listed hazardous wastes: halogenated degreasing solvents (F001), other halogenated solvents (F002), and non-halogenated solvents (F003-F005). The exact code depends on composition and prior use.',
    contexts: [
      { context: 'Halogenated solvents used in degreasing', code: 'F001' },
      { context: 'Other spent halogenated solvents', code: 'F002' },
      { context: 'Non-halogenated solvents (acetone, xylene)', code: 'F003' },
      { context: 'Non-halogenated toxic solvents (toluene, MEK)', code: 'F005' },
    ],
  },
  {
    slug: 'lead-acid-batteries',
    name: 'Lead-Acid Batteries',
    synonyms: ['car batteries', 'automotive batteries', 'UPS batteries', 'traction batteries'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Whole lead-acid batteries sent for reclamation are exempt under 40 CFR 266. Discarded batteries carry D008 (lead) and D002 (corrosive electrolyte) unless managed for recycling.',
    contexts: [
      { context: 'Discarded, not sent for reclamation - lead', code: 'D008' },
      { context: 'Discarded, not sent for reclamation - corrosive electrolyte', code: 'D002' },
      { context: 'Sent for reclamation/recycling', note: 'Exempt under 40 CFR 266.40 if managed as used or refurbished battery' },
    ],
  },
  {
    slug: 'used-oil',
    name: 'Used Oil',
    synonyms: ['waste oil', 'used motor oil', 'used lubricating oil', 'gear oil waste'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Used oil destined for recycling is managed under 40 CFR Part 279, not as hazardous waste. Disposed used oil is hazardous only if it exhibits a characteristic (D001 if ignitable, D008 if lead exceeds TCLP threshold).',
    contexts: [
      { context: 'Recycled / re-refined / burned for energy', note: '40 CFR Part 279 standards, no RCRA waste code' },
      { context: 'Disposed and ignitable (flash point below 60C)', code: 'D001' },
      { context: 'Disposed with lead above 5.0 mg/L TCLP', code: 'D008' },
    ],
  },
  {
    slug: 'electroplating-sludge',
    name: 'Electroplating Sludge',
    synonyms: ['plating bath sludge', 'metal finishing waste', 'rinse water sludge'],
    rcraStatus: 'listed',
    summary:
      'Wastewater treatment sludges from electroplating operations are F006, an F-listed hazardous waste. Plating bath residues from the bottom of baths are F008, and cyanide-containing plating bath solutions are F007.',
    contexts: [
      { context: 'Wastewater treatment sludges from electroplating', code: 'F006' },
      { context: 'Plating bath residues from bottom of baths', code: 'F008' },
      { context: 'Cyanide plating bath solutions', code: 'F007' },
    ],
  },
  {
    slug: 'paint-waste',
    name: 'Paint Waste',
    synonyms: ['waste paint', 'paint residue', 'paint sludge', 'lacquer waste'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Paint waste may be hazardous if it exhibits ignitability (D001 if flash point below 60C) or contains lead/chromium above TCLP thresholds (D008/D007). Water-based paints with no hazardous constituents may not be RCRA hazardous.',
    contexts: [
      { context: 'Solvent-based paint (typically ignitable)', code: 'D001' },
      { context: 'Paint containing lead (above 5.0 mg/L TCLP)', code: 'D008' },
      { context: 'Paint containing chromium (above 5.0 mg/L TCLP)', code: 'D007' },
      { context: 'Water-based paint without hazardous constituents', note: 'May not be RCRA hazardous; check TCLP' },
    ],
  },
  {
    slug: 'aerosol-cans',
    name: 'Aerosol Cans',
    synonyms: ['pressurized cans', 'spray cans', 'spray paint cans'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Aerosol cans with residual product or propellant are hazardous if contents are ignitable (D001) or toxic. Fully depressurized, punctured cans may qualify for universal waste or normal recycling depending on residue.',
    contexts: [
      { context: 'Aerosols with ignitable product/propellant', code: 'D001' },
      { context: 'Aerosols with toxic contents', code: 'D006' },
      { context: 'Fully emptied and depressurized', note: 'May be managed as universal waste or scrap metal if thoroughly cleaned' },
    ],
  },
  {
    slug: 'pesticides',
    name: 'Pesticides & Herbicides',
    synonyms: ['pesticide waste', 'herbicide waste', 'agricultural chemicals', 'off-spec pesticide'],
    rcraStatus: 'listed',
    summary:
      'Unused pesticide and herbicide products are P-listed (acutely hazardous) or U-listed (toxic), depending on active ingredient. Empty containers may retain hazardous residues.',
    contexts: [
      { context: 'Acutely hazardous pesticides (various)', code: 'P001' },
      { context: 'Toxic pesticide waste (various)', code: 'U002' },
      { context: 'Empty pesticide containers with residues', note: 'Treated as if containing the original product until triple-rinsed' },
    ],
  },
  {
    slug: 'fluorescent-tubes',
    name: 'Fluorescent Tubes',
    synonyms: ['fluorescent lamps', 'mercury lamps', 'CFL bulbs', 'strip lights'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Mercury-containing lamps are considered universal waste under 40 CFR Part 273. Under RCRA hazardous waste characterization, they may carry D009 (mercury above 0.2 mg/L TCLP) unless managed as universal waste.',
    contexts: [
      { context: 'Mercury-containing lamps (universal waste path preferred)', note: '40 CFR Part 273 standards' },
      { context: 'If characterized as hazardous waste - mercury content', code: 'D009' },
      { context: 'Ballasts containing PCB', note: 'Additional hazardous waste concern' },
    ],
  },
  {
    slug: 'lab-chemicals',
    name: 'Laboratory Chemicals',
    synonyms: ['lab waste', 'research chemicals', 'chemical reagents', 'expired chemicals'],
    rcraStatus: 'listed',
    summary:
      'Discarded laboratory chemicals are P-listed or U-listed depending on the specific chemical. Mixtures of unknown or multiple hazardous chemicals are typically managed as hazardous waste due to prudent assumption.',
    contexts: [
      { context: 'Acutely hazardous lab chemicals', code: 'P001' },
      { context: 'Toxic lab chemicals', code: 'U002' },
      { context: 'Unknown mixture of lab chemicals', note: 'Assume hazardous per EPA guidance' },
    ],
  },
  {
    slug: 'antifreeze',
    name: 'Antifreeze & Coolant',
    synonyms: ['engine coolant', 'glycol coolant', 'vehicle coolant', 'radiator fluid'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Used antifreeze may be hazardous if it contains lead or exhibits toxicity characteristics. Many modern coolants are not RCRA hazardous, but used automotive coolant is often assumed hazardous and characterized.',
    contexts: [
      { context: 'Antifreeze with lead (above 5.0 mg/L TCLP)', code: 'D008' },
      { context: 'Antifreeze with other heavy metals', code: 'D006' },
      { context: 'Modern lead-free antifreeze', note: 'May not be RCRA hazardous but verify TCLP' },
    ],
  },
  {
    slug: 'oily-rags',
    name: 'Oily Rags & Absorbents',
    synonyms: ['oil-soaked rags', 'solvent-saturated wipes', 'contaminated absorbents', 'cleanup materials'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Rags and absorbents contaminated with ignitable solvents (flash point below 60C) or oils may carry D001. Rags with hazardous heavy metals carry the corresponding D-code (D008 for lead, D009 for mercury).',
    contexts: [
      { context: 'Rags/absorbents with ignitable solvent or oil', code: 'D001' },
      { context: 'Containing lead', code: 'D008' },
      { context: 'Containing mercury', code: 'D009' },
      { context: 'Uncontaminated rags and absorbents', note: 'Not RCRA hazardous waste' },
    ],
  },
  {
    slug: 'sandblasting-grit',
    name: 'Sandblasting Grit & Media',
    synonyms: ['blast media', 'shot blast waste', 'abrasive media waste', 'spent grit'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Spent blasting material used to remove lead paint or hazardous coatings carries D008 (lead). Clean blasting material from non-hazardous surfaces may not be RCRA hazardous.',
    contexts: [
      { context: 'Blasting media from lead paint removal', code: 'D008' },
      { context: 'Media from removing other hazardous coatings', code: 'D006' },
      { context: 'Clean media from non-hazardous surfaces', note: 'Likely not RCRA hazardous' },
    ],
  },
  {
    slug: 'pickle-liquor',
    name: 'Pickle Liquor & Steel Pickling Waste',
    synonyms: ['pickling acid', 'steel pickling waste', 'acid dip liquor', 'pickling bath'],
    rcraStatus: 'listed',
    summary:
      'Spent metal pickling solutions and sludges are K-listed wastes from iron and steel production. The specific K-code depends on whether the material is from blast furnace, steelmaking, or rolled steel operations.',
    contexts: [
      { context: 'Spent pickling liquor (steel mills)', code: 'K062' },
      { context: 'Sludges from pickling operations', note: 'Classified as K-listed industrial waste' },
    ],
  },

  // ---- Not-regulated (portability from EWC_LOOKUP where same material exists) ----
  {
    slug: 'cardboard',
    name: 'Cardboard',
    synonyms: ['corrugated cardboard', 'OCC', 'cardboard boxes', 'carton'],
    rcraStatus: 'not-regulated',
    summary:
      'Cardboard is not a federal hazardous waste under RCRA. It is regulated as solid waste under state and local rules; clean OCC is a traded recovered commodity.',
    contexts: [
      { context: 'Clean commercial/industrial OCC', note: 'Recovered paper commodity — typically sold, not disposed' },
      { context: 'Contaminated with listed waste (e.g. solvent-soaked)', note: 'The contaminating waste code governs, not the cardboard' },
    ],
  },
  {
    slug: 'paper',
    name: 'Paper',
    synonyms: ['office paper', 'mixed paper', 'newspaper', 'shredded paper'],
    rcraStatus: 'not-regulated',
    summary:
      'Paper is not a federal hazardous waste under RCRA. It is solid waste under state/local jurisdiction. Separately collected office and municipal paper is a recovered commodity.',
    contexts: [
      { context: 'Office and municipal paper', note: 'Not RCRA hazardous — state/local solid waste rules apply' },
      { context: 'Contaminated paper (oil-soaked, chemical-saturated)', note: 'The contaminant code governs' },
    ],
  },
  {
    slug: 'plastic-packaging',
    name: 'Plastic Packaging',
    synonyms: ['plastic wrap', 'shrink wrap', 'plastic bags', 'PET bottles'],
    rcraStatus: 'not-regulated',
    summary:
      'Plastic packaging (film, bottles, crates) is not a federal hazardous waste under RCRA when clean. It is regulated as solid waste under state/local rules and is a recovered commodity when separately collected.',
    contexts: [
      { context: 'Clean plastic packaging', note: 'Not RCRA hazardous' },
      { context: 'Contaminated plastic (pesticide bottles, chemical containers)', note: 'The residual chemical code governs' },
    ],
  },
  {
    slug: 'wooden-pallets',
    name: 'Wooden Pallets',
    synonyms: ['pallets', 'wooden packaging', 'crates', 'timber packaging'],
    rcraStatus: 'not-regulated',
    summary:
      'Wooden pallets are not federal hazardous waste under RCRA. They are solid waste regulated under state rules. Untreated pallets are often recovered for resale or are biodegradable waste.',
    contexts: [
      { context: 'Untreated wooden pallets', note: 'Not RCRA hazardous — state/local solid waste jurisdiction' },
      { context: 'Pallets treated with hazardous preservatives (creosote, CCA)', note: 'Contact waste classification authority' },
    ],
  },
  {
    slug: 'metal-packaging',
    name: 'Metal Packaging',
    synonyms: ['tins', 'cans', 'steel drums', 'aluminium cans'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean metallic packaging (cans, tins, drums) is not a federal hazardous waste under RCRA. It is a scrap metal commodity. Drums or containers with residues of hazardous substances carry the hazardous substance code.',
    contexts: [
      { context: 'Clean metallic packaging', note: 'Scrap metal commodity — not RCRA hazardous' },
      { context: 'Containers with hazardous residues', note: 'The residual chemical code governs (e.g., F006 for plating sludge residue)' },
    ],
  },
  {
    slug: 'glass-bottles',
    name: 'Glass Bottles',
    synonyms: ['glass packaging', 'bottle glass', 'container glass'],
    rcraStatus: 'not-regulated',
    summary:
      'Glass packaging (bottles, jars) is not a federal hazardous waste under RCRA when clean. It is a recovered commodity and solid waste under state/local jurisdiction.',
    contexts: [
      { context: 'Clean glass packaging', note: 'Not RCRA hazardous — glass cullet commodity' },
      { context: 'Contaminated glass (chemical residue)', note: 'The residual substance code governs' },
    ],
  },
  {
    slug: 'food-waste',
    name: 'Food Waste',
    synonyms: ['kitchen waste', 'catering waste', 'organic waste', 'biowaste'],
    rcraStatus: 'not-regulated',
    summary:
      'Food waste is not a federal hazardous waste under RCRA. Biodegradable organic waste is regulated under state/local solid waste and compost rules.',
    contexts: [
      { context: 'Kitchen, canteen, catering waste', note: 'Not RCRA hazardous — solid waste jurisdiction' },
      { context: 'Food manufacturing byproducts', note: 'Depends on processing; some are animal feed, some are compostable' },
    ],
  },
  {
    slug: 'garden-waste',
    name: 'Garden Waste',
    synonyms: ['green waste', 'landscaping waste', 'grass cuttings', 'tree prunings'],
    rcraStatus: 'not-regulated',
    summary:
      'Garden and yard waste is not a federal hazardous waste under RCRA. Biodegradable waste is regulated as solid waste under state/local compost and organics rules.',
    contexts: [
      { context: 'Grass, leaves, branches, prunings', note: 'Not RCRA hazardous — compostable or chippable' },
    ],
  },
  {
    slug: 'textiles',
    name: 'Textiles',
    synonyms: ['fabric waste', 'clothing', 'clothes', 'rags', 'garments'],
    rcraStatus: 'not-regulated',
    summary:
      'Textile waste is not a federal hazardous waste under RCRA when uncontaminated. It is solid waste regulated under state/local rules and often recovered for resale or reuse.',
    contexts: [
      { context: 'Clothing, linens, fabric waste', note: 'Not RCRA hazardous' },
      { context: 'Textiles contaminated with hazardous substances', note: 'The contaminant code governs' },
    ],
  },
  {
    slug: 'wood',
    name: 'Wood & Timber',
    synonyms: ['timber', 'untreated wood', 'wood offcuts', 'joinery waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Untreated wood is not a federal hazardous waste under RCRA. It is solid waste regulated under state/local rules and often recovered for mulch, energy, or remanufacturing.',
    contexts: [
      { context: 'Untreated wood (sawdust, offcuts, pallets)', note: 'Not RCRA hazardous' },
      { context: 'Treated wood (creosote, CCA preservatives)', note: 'Contact waste authority — may be hazardous' },
    ],
  },
  {
    slug: 'concrete',
    name: 'Concrete',
    synonyms: ['concrete rubble', 'crushed concrete', 'concrete waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean concrete rubble is not a federal hazardous waste under RCRA. It is inert solid waste recovered for aggregate or landfilled under state/local rules.',
    contexts: [
      { context: 'Clean demolition concrete', note: 'Inert waste — not RCRA hazardous' },
      { context: 'Concrete with hazardous contamination', note: 'The contaminant code governs' },
    ],
  },
  {
    slug: 'bricks',
    name: 'Bricks & Masonry',
    synonyms: ['brick rubble', 'brick waste', 'ceramic masonry'],
    rcraStatus: 'not-regulated',
    summary:
      'Brick and masonry waste is not a federal hazardous waste under RCRA. It is inert solid waste often recovered for aggregate or construction fill.',
    contexts: [
      { context: 'Clean brick rubble', note: 'Inert waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'soil-and-stones',
    name: 'Soil & Stones',
    synonyms: ['excavated soil', 'subsoil', 'spoil', 'muckaway'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean excavated soil and stone is not a federal hazardous waste under RCRA. They are inert materials recovered for fill, landscaping, or landfilled under state/local rules.',
    contexts: [
      { context: 'Clean excavation soil', note: 'Inert material — not RCRA hazardous' },
      { context: 'Contaminated soil (petroleum, hazardous substance)', note: 'The contaminant code governs' },
    ],
  },
  {
    slug: 'asphalt',
    name: 'Asphalt',
    synonyms: ['bituminous mixtures', 'road planings', 'tarmac', 'asphalt millings'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean asphalt pavement milling is not a federal hazardous waste under RCRA. It is recycled for road base or recovered as aggregate. Tar-bound material (pre-1980 roads) may contain hazardous coal tar.',
    contexts: [
      { context: 'Clean bituminous pavement (post-1980)', note: 'Not RCRA hazardous when recycled' },
      { context: 'Coal-tar-bound material (pre-1980 roads)', note: 'May be hazardous — verify coal tar content' },
    ],
  },
  {
    slug: 'electronic-waste',
    name: 'Electronic Waste (WEEE)',
    synonyms: ['WEEE', 'e-waste', 'computers', 'monitors', 'IT equipment'],
    rcraStatus: 'not-regulated',
    summary:
      'Discarded electronics are not universally regulated under federal RCRA. They are managed under state e-waste or universal waste rules. RCRA applies only if hazardous components (mercury lamps, CRT glass, lead solder) exceed thresholds.',
    contexts: [
      { context: 'Modern electronics without hazardous components', note: 'State e-waste regulations apply, not RCRA' },
      { context: 'Equipment with mercury lamps or CRTs', note: 'May trigger universal waste or state hazardous waste rules' },
    ],
  },
  {
    slug: 'batteries',
    name: 'Batteries',
    synonyms: ['accumulators', 'battery waste', 'household batteries', 'alkaline batteries'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Consumer batteries (alkaline, zinc-carbon) typically have no RCRA code. Rechargeable batteries (Ni-Cd, lithium) may be hazardous. Batteries containing mercury or lead are universal waste under 40 CFR Part 273.',
    contexts: [
      { context: 'Alkaline and zinc-carbon batteries', note: 'No RCRA code — managed under state battery regulations' },
      { context: 'Ni-Cd and lithium batteries', note: 'May be hazardous or universal waste — verify state rules' },
      { context: 'Lead-acid batteries', code: 'D008' },
    ],
  },
  {
    slug: 'cooking-oil',
    name: 'Cooking Oil & Grease',
    synonyms: ['used cooking oil', 'UCO', 'edible oil', 'fryer oil'],
    rcraStatus: 'not-regulated',
    summary:
      'Used edible oils and fats have no RCRA code when managed for recycling (rendering, biodiesel). They are not regulated under federal RCRA, but state food waste and used oil merchant rules apply.',
    contexts: [
      { context: 'Edible oil and fat for rendering/biodiesel', note: 'Not RCRA hazardous — state regulatory jurisdiction' },
      { context: 'Grease-trap waste (mixed food and petroleum oils)', note: 'May be hazardous if petroleum-based; verify composition' },
    ],
  },
  {
    slug: 'bulky-waste',
    name: 'Bulky Waste',
    synonyms: ['mattresses', 'furniture', 'sofas', 'large household items'],
    rcraStatus: 'not-regulated',
    summary:
      'Bulky household items (furniture, mattresses) are not a federal hazardous waste under RCRA. They are solid waste regulated under state/local municipal waste rules.',
    contexts: [
      { context: 'Bulky municipal waste', note: 'Not RCRA hazardous — landfill or specialty disposal jurisdiction' },
    ],
  },
  {
    slug: 'mixed-municipal-waste',
    name: 'Mixed Municipal Waste',
    synonyms: ['general waste', 'black bag waste', 'residual waste', 'household waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Mixed unsegregated household and municipal waste is not a federal hazardous waste under RCRA. It is solid waste under state/local municipal disposal rules.',
    contexts: [
      { context: 'Mixed residential/commercial waste', note: 'Solid waste — state jurisdiction, not RCRA' },
    ],
  },
  {
    slug: 'paint-filters',
    name: 'Paint Filters & Residue',
    synonyms: ['paint filter cake', 'spray booth filters', 'filter residue'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Paint filters and filter cake from spray operations are hazardous if the paint is ignitable or contains heavy metals. D001 applies to solvent-based; D008 to lead-containing.',
    contexts: [
      { context: 'Filters from solvent-based paint operations', code: 'D001' },
      { context: 'Filters from lead-based paint operations', code: 'D008' },
    ],
  },
  {
    slug: 'foundry-sand',
    name: 'Foundry Sand',
    synonyms: ['casting sand', 'moulding sand', 'spent foundry sand'],
    rcraStatus: 'not-regulated',
    summary:
      'Spent foundry sand is typically not a federal hazardous waste under RCRA unless it exhibits characteristics or contains hazardous residues from the casting or metal process.',
    contexts: [
      { context: 'Clean foundry sand', note: 'Not RCRA hazardous when free of binding compounds' },
      { context: 'Sand contaminated with hazardous metals', note: 'The metal code governs' },
    ],
  },
  {
    slug: 'hydraulic-fluid',
    name: 'Hydraulic Fluid',
    synonyms: ['hydraulic oil', 'hydraulic waste'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Used hydraulic fluid is hazardous if it is ignitable (D001 if flash point below 60C) or contains heavy metals. Recycled hydraulic fluid may not be RCRA hazardous.',
    contexts: [
      { context: 'Mineral-based hydraulic oil (disposed)', code: 'D001' },
      { context: 'Recycled/re-refined hydraulic fluid', note: 'Not RCRA hazardous if properly reclaimed' },
    ],
  },
  {
    slug: 'compressed-gas-cylinders',
    name: 'Compressed Gas Cylinders',
    synonyms: ['pressure containers', 'LPG bottles', 'propane cylinders'],
    rcraStatus: 'not-regulated',
    summary:
      'Empty compressed gas cylinders are not a federal hazardous waste under RCRA if properly depressurized and purged. They may be recycled as scrap metal. Cylinders with hazardous residual gas require specialized handling.',
    contexts: [
      { context: 'Cylinders with hazardous residual gas', note: 'Requires specialized hazardous waste contractor' },
      { context: 'Properly depressurized and purged cylinders', note: 'May be recycled as scrap metal after certification' },
    ],
  },
  {
    slug: 'insulation',
    name: 'Insulation Materials',
    synonyms: ['rockwool', 'glass wool', 'mineral wool', 'PIR boards'],
    rcraStatus: 'not-regulated',
    summary:
      'Fiberglass and mineral wool insulation are not a federal hazardous waste under RCRA. They are inert solid waste. Asbestos-containing insulation is hazardous and requires specialized disposal.',
    contexts: [
      { context: 'Fiberglass, mineral wool, rockwool', note: 'Not RCRA hazardous — state/local rules' },
      { context: 'Asbestos-containing insulation', note: 'Contact environmental authority — hazardous' },
    ],
  },
  {
    slug: 'toner-cartridges',
    name: 'Toner Cartridges',
    synonyms: ['printer cartridges', 'ink cartridges', 'toner', 'copier toner'],
    rcraStatus: 'not-regulated',
    summary:
      'Spent toner cartridges are typically not a federal hazardous waste under RCRA. They are managed under state e-waste, universal waste, or manufacturer take-back programs.',
    contexts: [
      { context: 'Toner cartridges', note: 'Manufacturer take-back or state e-waste program — not RCRA' },
    ],
  },
  {
    slug: 'scrap-metal',
    name: 'Scrap Metal',
    synonyms: ['metal scrap', 'ferrous metal', 'non-ferrous metal', 'steel scrap'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean scrap metal is not a federal hazardous waste under RCRA. It is a commodity recovered for recycling. Metal contaminated with hazardous substances carries the contaminant code.',
    contexts: [
      { context: 'Clean ferrous and non-ferrous metal', note: 'Commodity — not RCRA hazardous' },
      { context: 'Metal contaminated (oil, solvents, heavy metals)', note: 'The contaminant code governs' },
    ],
  },
  {
    slug: 'copper-wire-and-cable',
    name: 'Copper Wire & Cable',
    synonyms: ['copper scrap', 'electrical cable', 'copper tubing'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean copper wire and cable are scrap metal commodities, not federal hazardous waste under RCRA. Cables with oil or tar impregnation may carry the contaminant code.',
    contexts: [
      { context: 'Clean copper wire', note: 'Scrap metal commodity — not RCRA hazardous' },
      { context: 'Oil-soaked or tar-soaked cable', note: 'The oil/tar code governs' },
    ],
  },
  {
    slug: 'aluminum-scrap',
    name: 'Aluminum Scrap',
    synonyms: ['aluminium scrap', 'aluminum profiles', 'aluminum sheet'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean aluminum scrap is not a federal hazardous waste under RCRA. It is a commodity recovered for recycling under scrap-metal commodity rules.',
    contexts: [
      { context: 'Clean aluminum scrap', note: 'Commodity — not RCRA hazardous' },
    ],
  },
  {
    slug: 'plasterboard',
    name: 'Plasterboard',
    synonyms: ['drywall', 'gypsum board', 'wallboard', 'gib board'],
    rcraStatus: 'not-regulated',
    summary:
      'Gypsum board is not a federal hazardous waste under RCRA. It is inert solid waste managed under state/local construction debris rules.',
    contexts: [
      { context: 'Plasterboard', note: 'Inert waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'roof-shingles',
    name: 'Roof Shingles',
    synonyms: ['asphalt shingles', 'roofing waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Asphalt roof shingles are not a federal hazardous waste under RCRA. They are inert construction waste often recovered for road base or landfilled.',
    contexts: [
      { context: 'Asphalt shingles', note: 'Inert construction waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'vinyl-flooring',
    name: 'Vinyl Flooring',
    synonyms: ['VCT', 'linoleum', 'resilient flooring'],
    rcraStatus: 'not-regulated',
    summary:
      'Vinyl flooring and linoleum are not a federal hazardous waste under RCRA. They are solid waste. Some vintage vinyl may contain asbestos or plasticizer concerns; verify composition.',
    contexts: [
      { context: 'Modern vinyl flooring', note: 'Solid waste — not RCRA hazardous' },
      { context: 'Pre-1980 vinyl flooring', note: 'May contain asbestos or phthalates — verify' },
    ],
  },
  {
    slug: 'rubber-tires',
    name: 'Rubber Tires',
    synonyms: ['waste tires', 'end-of-life tires', 'ELT', 'scrap tires'],
    rcraStatus: 'not-regulated',
    summary:
      'Used tires are not a federal hazardous waste under RCRA. They are managed under state used-tire programs (landfill bans in many states). Some states allow ground rubber or tire-derived fuel.',
    contexts: [
      { context: 'Used tires', note: 'State tire program jurisdiction, not RCRA' },
    ],
  },
  {
    slug: 'carpet-and-underlayment',
    name: 'Carpet & Underlayment',
    synonyms: ['carpet tiles', 'floor covering', 'carpet underlayment'],
    rcraStatus: 'not-regulated',
    summary:
      'Carpet and underlayment are not a federal hazardous waste under RCRA. They are solid waste managed under state municipal or construction debris rules.',
    contexts: [
      { context: 'Carpet and underlayment', note: 'Solid waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'polyurethane-foam',
    name: 'Polyurethane Foam',
    synonyms: ['foam waste', 'cushioning foam', 'insulation foam'],
    rcraStatus: 'not-regulated',
    summary:
      'Polyurethane foam is not a federal hazardous waste under RCRA. It is solid waste. Some rigid foam insulation may contain ozone-depleting substances (CFC/HCFC) requiring special handling.',
    contexts: [
      { context: 'Polyurethane foam', note: 'Solid waste — not RCRA hazardous if CFC-free' },
      { context: 'Foam containing CFC/HCFC blowing agents', note: 'May require state/federal environmental review' },
    ],
  },
  {
    slug: 'fiberglass-reinforced-plastic',
    name: 'Fiberglass Reinforced Plastic',
    synonyms: ['FRP', 'fiberglass waste', 'composite waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Fiberglass-reinforced plastic is not a federal hazardous waste under RCRA. It is solid waste. FRP waste is difficult to recycle and often landfilled; some states are developing composites programs.',
    contexts: [
      { context: 'FRP scrap and waste', note: 'Solid waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'plastic-film',
    name: 'Plastic Film Waste',
    synonyms: ['plastic wrap', 'shrink wrap', 'plastic sheet'],
    rcraStatus: 'not-regulated',
    summary:
      'Plastic film is not a federal hazardous waste under RCRA when clean. It is solid waste recovered for recycling or landfilled.',
    contexts: [
      { context: 'Clean plastic film', note: 'Solid waste commodity — not RCRA hazardous' },
    ],
  },
  {
    slug: 'fiberglass-strands',
    name: 'Fiberglass Strands & Offcuts',
    synonyms: ['fiberglass waste', 'glass fiber waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Fiberglass strands and manufacturing offcuts are not a federal hazardous waste under RCRA. They are solid waste from composite manufacturing.',
    contexts: [
      { context: 'Fiberglass manufacturing waste', note: 'Solid waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'polymeric-waste',
    name: 'Polymeric Waste',
    synonyms: ['plastic waste', 'polymer offcuts', 'regrind'],
    rcraStatus: 'not-regulated',
    summary:
      'Plastic and polymer waste is not a federal hazardous waste under RCRA when uncontaminated. Clean plastic offcuts and regrinds are recovered commodities.',
    contexts: [
      { context: 'Clean plastic offcuts and regrinds', note: 'Commodity — not RCRA hazardous' },
    ],
  },
  {
    slug: 'textile-manufacturing-waste',
    name: 'Textile Manufacturing Waste',
    synonyms: ['fabric offcuts', 'yarn waste', 'fiber waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Textile manufacturing offcuts and waste are not a federal hazardous waste under RCRA. They are solid waste, often recovered for fiber or filler uses.',
    contexts: [
      { context: 'Clean textile waste', note: 'Solid waste — not RCRA hazardous' },
    ],
  },
  {
    slug: 'corrugated-metal',
    name: 'Corrugated Metal Roofing',
    synonyms: ['metal roofing', 'corrugated panels', 'metal sheeting'],
    rcraStatus: 'not-regulated',
    summary:
      'Corrugated metal roofing and sheeting are not a federal hazardous waste under RCRA when clean. They are scrap metal commodities.',
    contexts: [
      { context: 'Clean metal roofing and panels', note: 'Scrap commodity — not RCRA hazardous' },
    ],
  },
  {
    slug: 'firebrick-and-refractory',
    name: 'Firebrick & Refractory Materials',
    synonyms: ['refractory brick', 'kiln brick', 'furnace brick'],
    rcraStatus: 'not-regulated',
    summary:
      'Spent firebrick and refractory materials are not a federal hazardous waste under RCRA unless they contain hazardous contaminants. Clean refractories are often recovered.',
    contexts: [
      { context: 'Clean spent firebrick', note: 'Inert material — not RCRA hazardous' },
      { context: 'Refractories with hazardous metal content', note: 'The metal code governs' },
    ],
  },
  {
    slug: 'coal-tar-roofing',
    name: 'Coal-Tar Roofing Material',
    synonyms: ['tar paper', 'coal tar pitch', 'roofing tar'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Coal-tar-based roofing materials and pitch may be hazardous due to PAHs (polycyclic aromatic hydrocarbons). They may exhibit toxicity characteristics depending on composition.',
    contexts: [
      { context: 'Coal-tar roofing and pitch (pre-1980)', code: 'D008' },
      { context: 'Modern bituminous roofing (non-tar)', note: 'Not RCRA hazardous' },
    ],
  },
  {
    slug: 'asbestos-containing-material',
    name: 'Asbestos-Containing Material',
    synonyms: ['ACM', 'asbestos waste', 'friable asbestos', 'non-friable asbestos'],
    rcraStatus: 'not-regulated',
    summary:
      'Asbestos-containing materials are not regulated under federal RCRA. They are managed under EPA NESHAP and state asbestos rules. Contact environmental authority for proper handling.',
    contexts: [
      { context: 'Friable asbestos insulation', note: 'Requires EPA NESHAP and state asbestos contractor' },
      { context: 'Non-friable bonded ACM (sheets, tiles)', note: 'EPA NESHAP and state rules apply' },
    ],
  },
  {
    slug: 'demolition-debris',
    name: 'Mixed Demolition Debris',
    synonyms: ['construction waste', 'building waste', 'C&D waste'],
    rcraStatus: 'not-regulated',
    summary:
      'Clean mixed demolition debris is not a federal hazardous waste under RCRA. It is inert solid waste managed under state construction debris and landfill rules.',
    contexts: [
      { context: 'Clean mixed demolition material', note: 'Inert waste — not RCRA hazardous' },
      { context: 'Debris containing hazardous components (asbestos, lead paint)', note: 'The hazardous component code governs' },
    ],
  },
  {
    slug: 'insulation-debris',
    name: 'Insulation Debris',
    synonyms: ['blown insulation', 'loose fill', 'batts and blankets'],
    rcraStatus: 'not-regulated',
    summary:
      'Fiberglass and mineral wool insulation debris are not a federal hazardous waste under RCRA. They are solid waste. Asbestos insulation must be handled separately.',
    contexts: [
      { context: 'Fiberglass or mineral wool insulation debris', note: 'Solid waste — not RCRA hazardous' },
      { context: 'Asbestos insulation', note: 'Requires specialized asbestos contractor per EPA' },
    ],
  },
  {
    slug: 'wastewater-treatment-sludge',
    name: 'Wastewater Treatment Sludge',
    synonyms: ['WWTP sludge', 'biosolids', 'treatment sludge'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Industrial wastewater treatment sludges may be RCRA hazardous if they exceed contaminant thresholds (D004-D043). Many sludges are managed under Clean Water Act 503 rules rather than RCRA.',
    contexts: [
      { context: 'Treatment sludge with heavy metals', code: 'D008' },
      { context: 'Treatment sludge with other contaminants', code: 'D006' },
      { context: 'Municipal biosolids (land application)', note: 'CWA 503 jurisdiction, not typically RCRA' },
    ],
  },
  {
    slug: 'lead-paint-chips',
    name: 'Lead Paint Chips & Debris',
    synonyms: ['lead paint waste', 'leaded paint debris'],
    rcraStatus: 'characteristic-possible',
    summary:
      'Paint chips and debris from lead-based paint removal may carry D008 (lead) if they exceed 5.0 mg/L TCLP threshold. Proper categorization requires testing.',
    contexts: [
      { context: 'Lead-based paint chips and debris', code: 'D008' },
      { context: 'Non-lead paint chips', note: 'Typically not RCRA hazardous unless other hazardous constituents present' },
    ],
  },
];


const bySlug = new Map(MATERIAL_RCRA_LOOKUP.map((m) => [m.slug, m]));

export function resolveMaterialRcraSlug(slug: string): MaterialRcraEntry | undefined {
  return bySlug.get(slug.toLowerCase().trim());
}

export function getMaterialLookupLinksForRcraCode(code: string): { slug: string; name: string }[] {
  const norm = code.toUpperCase().trim();
  return MATERIAL_RCRA_LOOKUP.filter((m) => m.contexts.some((c) => c.code === norm)).map((m) => ({
    slug: m.slug,
    name: m.name,
  }));
}

// --- SERP meta builders -----------------------------------------------
// GSC 2026-07-10: /rcra-code-for/spent-solvents at 249 impressions, 1 click
// (0.4% CTR). The old question-form title previewed no answer and the meta
// was the complete answer — nothing left to click for. New contract:
// answer the code in the title, tease criteria/disposal in the description.
// @source https://developers.google.com/search/docs/appearance/title-link

const TITLE_SUFFIX = ' | SymbioFlows';

/** Compact span over the entry's codes: "F001–F005", "D002 & D008", "D008". */
function codeSpan(entry: MaterialRcraEntry): string | null {
  const codes = [...new Set(
    entry.contexts.map((c) => c.code).filter((c): c is string => Boolean(c)),
  )].sort();
  if (codes.length === 0) return null;
  if (codes.length === 1) return codes[0];
  if (codes.length === 2) return `${codes[0]} & ${codes[1]}`;
  const sameList = codes.every((c) => c[0] === codes[0][0]);
  return sameList ? `${codes[0]}–${codes[codes.length - 1]}` : `${codes[0]} & more`;
}

export function materialRcraTitle(entry: MaterialRcraEntry): string {
  const span = codeSpan(entry);
  let base = span
    ? `RCRA Code for ${entry.name}: ${span} — Disposal Rules`
    : `RCRA Code for ${entry.name} — Exemptions & Disposal`;
  if (base.length > 60) {
    base = span ? `RCRA Code for ${entry.name}: ${span}` : `RCRA Code for ${entry.name}`;
  }
  return base.length + TITLE_SUFFIX.length <= 70 ? base + TITLE_SUFFIX : base;
}

export function materialRcraMetaDescription(entry: MaterialRcraEntry): string {
  const tail = entry.rcraStatus === 'not-regulated'
    ? ' See why, plus state-level rules and disposal options.'
    : ' See which code applies, disposal requirements and recycling routes.';
  const budget = 158 - tail.length;
  let lead = entry.summary.replace(/\s+/g, ' ').trim();
  if (lead.length > budget) {
    const cut = lead.slice(0, budget);
    lead = cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:.\s]+$/, '') + '.';
  }
  return lead + tail;
}
